import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape, type MessageDescriptor } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { Redirect, type RouteComponentProps } from "react-router-dom";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientId } from "~/actions/get_patient_id";
import { orderRetainers } from "~/actions/order_retainers";
import { updateMedia } from "~/actions/post_patient";
import { SOArch, SOMaterial } from "~/common/constants";
import { remoteLog } from "~/common/logging";
import { getLastCorrection, isPatient } from "~/common/patient";
import { PatientNewFiles } from "~/components/patient/patient_new/patient_new_files";
import { LabelledRadio, LabelledRadioGroup } from "~/components/ui/labelled-radio";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { LoadingButton } from "~/components/ui/loading-button";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { getExtraServices, selectServices } from "~/slices/services";
import type { RootState } from "~/store";

const mapStateToProps = (state: RootState) => ({
  patient: state.patient,
  media: state.media,
  services: selectServices(state),
});

const mapDispatchToProps = {
  updateMedia,
  eraseStateProp,
  getPatientId,
  orderRetainers,
  getExtraServices,
};

type PlanRetainersProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }> & {
    /** Render WITHOUT the legacy `<Layout>` chrome, for embedding inside the redesign shell. */
    bareLayout?: boolean;
  };

type PlanRetainersState = {
  isSubmitting: boolean;
  material: SOMaterial | null;
  arch: SOArch | null;
  quantity: number | null;
};

class PlanRetainers extends Component<PlanRetainersProps, PlanRetainersState> {
  constructor(props: PlanRetainersProps) {
    super(props);
    this.state = {
      isSubmitting: false,
      material: null,
      arch: null,
      quantity: null,
    };
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "3d_plan_retainers_page_body");
  }

  componentDidMount() {
    this.props.eraseStateProp("media");

    const { patient_id } = this.props.match.params;
    void this.props.getPatientId(Number(patient_id));
    void this.props.getExtraServices(Number(patient_id));

    setDocumentTitle(
      this.props.intl.formatMessage({ id: "pat.retainers.page.header" }),
      patient_id,
    );
  }

  componentDidUpdate(prevProps: PlanRetainersProps) {
    // Для новой версии курса "Безлимитный" (V2) при первом заказе ретейнеров устанавливаем значения по умолчанию
    if (isPatient(this.props.patient) && !isPatient(prevProps.patient)) {
      const { patient_id } = this.props.match.params;
      void this.props.getExtraServices(Number(patient_id));
    }

    if (isPatient(this.props.patient)) {
      const isLimitlessV2 = this.props.patient.course.course_option_tag === "LIMITLESS_V2"
        || this.props.patient.course.course_option_tag === "LIMITLESS_3_STEPS_V2";

      // Проверяем наличие ретейнеров в обоих источниках: payments.extra и services
      const hasRetainersInPayments = this.props.patient.course.payments?.extra?.some(
        (service) => service.tag?.startsWith("RETAINERS"),
      ) ?? false;

      const hasRetainersInServices = this.props.services?.some(
        (service) => service.tag?.startsWith("RETAINERS"),
      ) ?? false;

      const hasExistingRetainers = hasRetainersInPayments || hasRetainersInServices;
      const isFirstRetainersOrder = !hasExistingRetainers;

      if (isLimitlessV2 && isFirstRetainersOrder && (this.state.arch === null || this.state.quantity === null)) {
        // Устанавливаем значения по умолчанию: обе челюсти, 1 пара
        this.setState({
          arch: SOArch.BOTH,
          quantity: 1,
        });
      }
    }
  }

  async orderRetainersSubmit() {
    const { patient_id } = this.props.match.params;
    let { material, arch, quantity } = this.state;

    // Для новой версии курса "Безлимитный" (V2) при первом заказе устанавливаем значения по умолчанию, если они не установлены
    if (isPatient(this.props.patient)) {
      const isLimitlessV2 = this.props.patient.course.course_option_tag === "LIMITLESS_V2"
        || this.props.patient.course.course_option_tag === "LIMITLESS_3_STEPS_V2";

      // Проверяем наличие ретейнеров в обоих источниках: payments.extra и services
      const hasRetainersInPayments = this.props.patient.course.payments?.extra?.some(
        (service) => service.tag?.startsWith("RETAINERS"),
      ) ?? false;

      const hasRetainersInServices = this.props.services?.some(
        (service) => service.tag?.startsWith("RETAINERS"),
      ) ?? false;

      const hasExistingRetainers = hasRetainersInPayments || hasRetainersInServices;
      const isFirstRetainersOrder = !hasExistingRetainers;

      if (isLimitlessV2 && isFirstRetainersOrder) {
        // Устанавливаем значения по умолчанию: обе челюсти, 1 пара
        const newArch = arch ?? SOArch.BOTH;
        const newQuantity = quantity ?? 1;

        // Обновляем state для отображения и валидации
        if (this.state.arch !== newArch || this.state.quantity !== newQuantity) {
          this.setState({ arch: newArch, quantity: newQuantity });
          arch = newArch;
          quantity = newQuantity;
        }
      }
    }

    // Используем актуальные значения из state
    const finalArch = this.state.arch ?? arch;
    const finalQuantity = this.state.quantity ?? quantity;
    const finalMaterial = this.state.material ?? material;

    const validity = this.validateRequiredFields();

    $("#validation-files").css({ color: validity.files ? "#34495e" : "red" });
    $("#validation-material").css({ color: validity.material ? "#34495e" : "red" });
    $("#validation-arch").css({ color: validity.arch ? "#34495e" : "red" });
    $("#validation-quantity").css({ color: validity.quantity ? "#34495e" : "red" });

    if (Object.values(validity).some((v) => v == false)) {
      return;
    }

    this.setState({ isSubmitting: true });

    try {
      // Сначала загружаем медиа файлы (await критически важен - без него файлы не успевают обработаться)
      await this.props.updateMedia(Number(patient_id), {
        media: this.props.media,
        media_s3: {},
      });

      // Затем создаем заказ ретейнеров
      if (finalMaterial && finalArch && finalQuantity) {
        await this.props.orderRetainers(Number(patient_id), finalMaterial, finalArch, finalQuantity);
      }
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  validateRequiredFields() {
    const { material, arch, quantity } = this.state;

    const isScanUploaded = Object.entries(this.props.media).some(
      ([_, { user_filename }]) => user_filename?.endsWith(".stl"),
    );

    const areFilesValid = material != SOMaterial.USE_SCAN || isScanUploaded;
    const materialIsValid = material != null;
    const archIsValid = arch != null;
    const quantityIsValid = Boolean(quantity);

    return {
      files: areFilesValid,
      material: materialIsValid,
      arch: archIsValid,
      quantity: quantityIsValid,
    };
  }

  render() {
    const { patient } = this.props;
    const { material, isSubmitting, arch, quantity } = this.state;
    // Embedded in the redesign shell (`bareLayout`) the shell already supplies the page chrome, so the
    // legacy <Layout> would nest a second one. Fragment keeps the body identical either way.
    const Chrome = this.props.bareLayout ? React.Fragment : Layout;

    if (!isPatient(patient)) {
      return (
        <Chrome>
          <Loader />
        </Chrome>
      );
    }

    const { can_order_retainers } = getLastCorrection(patient).order_options;

    if (!can_order_retainers) {
      return <Redirect to="/pages/patients" />;
    }

    // Для новой версии курса "Безлимитный" (V2) при первом заказе ретейнеров блокируем выбор
    const isLimitlessV2 = patient.course.course_option_tag === "LIMITLESS_3_STEPS_V2";

    // Проверяем наличие ретейнеров в обоих источниках: payments.extra и services
    const hasRetainersInPayments = patient.course.payments?.extra?.some(
      (service) => service.tag?.startsWith("RETAINERS"),
    ) ?? false;

    const hasRetainersInServices = this.props.services?.some(
      (service) => service.tag?.startsWith("RETAINERS"),
    ) ?? false;

    const hasExistingRetainers = hasRetainersInPayments || hasRetainersInServices;
    const isFirstRetainersOrder = !hasExistingRetainers;
    const isLimitlessV2FirstOrder = isLimitlessV2 && isFirstRetainersOrder;

    return (
      <Chrome>
        <Portlet as="main" id="retainers-section-head">
          <PortletTitle iconClassName="icon-book-open" displayRequiredFieldsInfo>
            <FormattedMessage id="pat.retainers.page.header" />
          </PortletTitle>

          <div className="portlet-body">
            <form
              className="tw-space-y-5"
              onSubmit={(event) => {
                event.preventDefault();
                void this.orderRetainersSubmit();
              }}
            >
              <MaterialRadioGroup
                material={this.state.material}
                onMaterialChange={(newMaterial) => this.setState({ material: newMaterial })}
              />

              <PatientNewFiles required isVisible={material == SOMaterial.USE_SCAN} />

              {isLimitlessV2FirstOrder ? (
                <>
                  <LabelledRadioGroup
                    label={<FormattedMessage id="ARCHES_SELECT" />}
                    labelProps={{ id: "validation-arch" }}
                    required
                  >
                    <LabelledRadio
                      label={<FormattedMessage id="TA_BOTH" />}
                      name="treat_arches_id"
                      value={SOArch.BOTH}
                      checked={true}
                      disabled
                    />
                  </LabelledRadioGroup>

                  <LabelledRadioGroup
                    label={<FormattedMessage id="RETAINERS_QUANTITY" />}
                    labelProps={{ id: "validation-quantity" }}
                    required
                  >
                    <LabelledRadio
                      label="1"
                      name="retainers"
                      value={1}
                      checked={true}
                      disabled
                    />
                  </LabelledRadioGroup>
                </>
              ) : (
                <>
                  <ArchRadioGroup
                    arch={arch}
                    onArchChange={(newArch) => this.setState({ arch: newArch })}
                  />

                  <RetainersQuantityGroup
                    quantity={quantity}
                    onQuantityChange={(newQuantity) => this.setState({ quantity: newQuantity })}
                  />
                </>
              )}

              <LoadingButton
                id="submit-pacient-btn"
                variant="primary"
                size="xl"
                isLoading={isSubmitting}
              >
                <FormattedMessage id="pat.payments.buttons.submit" />
              </LoadingButton>
            </form>
          </div>
        </Portlet>
      </Chrome>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PlanRetainers));

function MaterialRadioGroup({
  material,
  onMaterialChange,
}: {
  material: SOMaterial | null;
  onMaterialChange(newMaterial: SOMaterial): void;
}) {
  type TOption = { value: SOMaterial; intlId: MessageDescriptor["id"] };

  const options: TOption[] = [
    { value: SOMaterial.LAST_STAGE, intlId: "EXTRA_SERVICE_OPTION_LAST_STAGE" },
    { value: SOMaterial.USE_SCAN, intlId: "EXTRA_SERVICE_OPTION_USE_SCAN" },
    { value: SOMaterial.USE_IMPRESSIONS, intlId: "EXTRA_SERVICE_OPTION_USE_IMPRESSIONS" },
  ];

  return (
    <LabelledRadioGroup
      label={<FormattedMessage id="ATTACHMENT_INSTRUCTIONS" />}
      labelProps={{ id: "validation-material" }}
      required
    >
      {options.map((option) => (
        <LabelledRadio
          key={option.value}
          label={<FormattedMessage id={option.intlId} />}
          name="material"
          value={option.value}
          checked={option.value == material}
          onChange={() => onMaterialChange(option.value)}
        />
      ))}
    </LabelledRadioGroup>
  );
}

function ArchRadioGroup({
  arch,
  onArchChange,
}: {
  arch: SOArch | null;
  onArchChange(newArch: SOArch): void;
}) {
  type TOption = { value: SOArch; intlId: MessageDescriptor["id"] };

  const options: TOption[] = [
    { value: SOArch.BOTH, intlId: "TA_BOTH" },
    { value: SOArch.UPPER, intlId: "TA_UPPER" },
    { value: SOArch.LOWER, intlId: "TA_LOWER" },
  ];

  return (
    <LabelledRadioGroup
      label={<FormattedMessage id="ARCHES_SELECT" />}
      labelProps={{ id: "validation-arch" }}
      required
    >
      {options.map((option) => (
        <LabelledRadio
          key={option.value}
          label={<FormattedMessage id={option.intlId} />}
          name="treat_arches_id"
          value={option.value}
          checked={option.value == arch}
          onChange={() => onArchChange(option.value)}
        />
      ))}
    </LabelledRadioGroup>
  );
}

export function RetainersQuantityGroup({
  quantity,
  onQuantityChange,
  className,
}: {
  quantity: number | null | undefined;
  onQuantityChange(newQuantity: number): void;
  className?: string;
}) {
  return (
    <LabelledRadioGroup
      label={<FormattedMessage id="RETAINERS_QUANTITY" />}
      labelProps={{ id: "validation-quantity" }}
      required
      className={className}
    >
      {[1, 2].map((option) => (
        <LabelledRadio
          key={option}
          label={option}
          name="retainers"
          value={option}
          checked={option == quantity}
          onChange={() => onQuantityChange(option)}
        />
      ))}
    </LabelledRadioGroup>
  );
}
